import React from "react";
import { createPortal } from "react-dom";

const LONG_PRESS_MS = 500;
const LONG_PRESS_MOVE_PX = 10;

/**
 * useLongPress — fires `callback` when the user holds a pointer on the element
 * for at least `ms` milliseconds without moving more than 10 px. Also
 * intercepts `contextmenu` (desktop right-click + iOS long-press callout) so
 * the host element can show a custom action sheet instead of the browser's
 * native selection menu.
 *
 * The host element should also apply `user-select: none` permanently to
 * prevent Safari/iOS from opening its text-selection UI before our timer
 * fires. See the `.verse-row` / `.ccc-row` CSS classes.
 */
export function useLongPress(callback, ms = LONG_PRESS_MS) {
    const timer = React.useRef(null);
    const start = React.useRef({ x: 0, y: 0 });
    const fired = React.useRef(false);

    const clear = React.useCallback(() => {
        if (timer.current) {
            clearTimeout(timer.current);
            timer.current = null;
        }
    }, []);

    const onPointerDown = React.useCallback((e) => {
        if (e.pointerType === "mouse" && e.button !== 0) return;
        fired.current = false;
        start.current = { x: e.clientX, y: e.clientY };
        clear();
        timer.current = setTimeout(() => {
            fired.current = true;
            callback(e);
        }, ms);
    }, [callback, ms, clear]);

    const onPointerMove = React.useCallback((e) => {
        if (!timer.current) return;
        const dx = (e.clientX ?? 0) - start.current.x;
        const dy = (e.clientY ?? 0) - start.current.y;
        if (Math.hypot(dx, dy) > LONG_PRESS_MOVE_PX) clear();
    }, [clear]);

    const onPointerUp = React.useCallback((e) => {
        clear();
        if (fired.current) {
            e.stopPropagation();
            fired.current = false;
        }
    }, [clear]);

    const onContextMenu = React.useCallback((e) => {
        e.preventDefault();
        callback(e);
    }, [callback]);

    return {
        onPointerDown,
        onPointerMove,
        onPointerUp,
        onPointerLeave: onPointerUp,
        onPointerCancel: onPointerUp,
        onContextMenu,
    };
}

/**
 * ContextMenu — floating action menu anchored to its parent element. Renders
 * via React portal into `document.body` so it is NEVER clipped by an
 * ancestor `overflow: hidden` (e.g. surface cards with rounded corners).
 *
 * Positioning: right-aligns to the host element's right edge, flips above
 * when there's not enough room below, and clamps to the viewport so the
 * menu can't bleed off-screen on small mobile widths.
 *
 * Props:
 *   items   — [{ id, label, icon?, onSelect }]
 *   onDismiss — called whenever the menu should close (outside click, scroll,
 *               Esc, or after an item is selected)
 *   testId  — base data-testid; each item gets `${testId}-item-${item.id}`
 */
export function ContextMenu({ items, onDismiss, testId = "context-menu" }) {
    const placeholderRef = React.useRef(null);
    const menuRef = React.useRef(null);
    // null = not measured yet → render hidden so we don't flash at (0,0).
    const [coords, setCoords] = React.useState(null);

    React.useLayoutEffect(() => {
        const placeholder = placeholderRef.current;
        if (!placeholder) return undefined;
        const host = placeholder.parentElement;
        if (!host) return undefined;

        const measure = () => {
            const menuEl = menuRef.current;
            if (!menuEl) return;
            const rect = host.getBoundingClientRect();
            const menuH = menuEl.offsetHeight || 200;
            const menuW = menuEl.offsetWidth || 240;
            const margin = 8;
            const spaceBelow = window.innerHeight - rect.bottom;
            const spaceAbove = rect.top;
            const placement = (spaceBelow < menuH + margin && spaceAbove > spaceBelow)
                ? "above" : "below";

            // Right-align to host's right edge. The dots-button is always
            // on the right, so this keeps the menu visually attached to it.
            let left = rect.right - menuW;
            // Clamp to viewport with 8 px gutters.
            left = Math.max(8, Math.min(left, window.innerWidth - menuW - 8));
            const top = placement === "below"
                ? rect.bottom + 4
                : rect.top - menuH - 4;
            setCoords({ left, top, placement });
        };

        // First pass to size the menu, then a second pass next frame once
        // dimensions stabilise (custom fonts swap in, icons hydrate, etc).
        measure();
        const id = requestAnimationFrame(measure);
        return () => cancelAnimationFrame(id);
    }, []);

    React.useEffect(() => {
        const onDown = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) onDismiss();
        };
        const onKey = (e) => { if (e.key === "Escape") onDismiss(); };
        const onScroll = () => onDismiss();
        const id = setTimeout(() => {
            document.addEventListener("pointerdown", onDown, true);
            document.addEventListener("keydown", onKey);
            window.addEventListener("scroll", onScroll, { passive: true, capture: true });
        }, 0);
        return () => {
            clearTimeout(id);
            document.removeEventListener("pointerdown", onDown, true);
            document.removeEventListener("keydown", onKey);
            window.removeEventListener("scroll", onScroll, { capture: true });
        };
    }, [onDismiss]);

    return (
        <>
            {/* Invisible placeholder kept inside the host so existing
                call-sites (`{menuOpen && <ContextMenu .../>}`) continue to
                "anchor" the menu to the surrounding action row without
                requiring an API change. */}
            <span ref={placeholderRef} className="hidden" aria-hidden="true" />
            {createPortal(
                <span
                    ref={menuRef}
                    role="menu"
                    data-testid={testId}
                    data-placement={coords?.placement || "below"}
                    onClick={(e) => e.stopPropagation()}
                    onPointerDown={(e) => e.stopPropagation()}
                    onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); }}
                    style={{
                        position: "fixed",
                        left: coords ? coords.left : -9999,
                        top: coords ? coords.top : -9999,
                        visibility: coords ? "visible" : "hidden",
                    }}
                    className="z-50 flex flex-col bg-white border border-sand-300 shadow-lg rounded-lg py-1 ui-sans text-sm min-w-[220px] max-w-[90vw]"
                >
                    {items.map((it, i) => (
                        <React.Fragment key={it.id}>
                            {i > 0 && <span className="h-px bg-sand-300 mx-2" aria-hidden="true" />}
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    it.onSelect();
                                    onDismiss();
                                }}
                                data-testid={`${testId}-item-${it.id}`}
                                className="inline-flex items-center gap-2 px-4 py-2.5 text-left hover:bg-sangre/5 text-stone900 hover:text-sangre transition-colors"
                            >
                                {it.icon}
                                <span>{it.label}</span>
                            </button>
                        </React.Fragment>
                    ))}
                </span>,
                document.body,
            )}
        </>
    );
}
